// src/core/storage.ts

import type { ConsentState, ConsentRecord, RemoteConfig } from '../types'
import { STORAGE_KEYS } from '../constants/defaults'
import { debugLog, setCookie, deleteCookie } from '../utils/helpers'

/**
 * Save consent to localStorage
 */
export function saveConsent(
  consent: ConsentState,
  version: string,
  language: string,
  ttl: number,
  domain?: string
): void {
  if (typeof window === 'undefined') return

  const record: ConsentRecord = {
    version,
    timestamp: Date.now(),
    choices: consent,
    userAgent: navigator.userAgent,
    language,
  }

  try {
    // Save to localStorage
    localStorage.setItem(STORAGE_KEYS.CONSENT, JSON.stringify(record))

    // Also save simplified version to cookie for server-side access
    const consentString = Object.entries(consent)
      .filter(([_, value]) => value)
      .map(([key]) => key)
      .join(',')

    setCookie(STORAGE_KEYS.CONSENT, consentString, ttl, domain)

    debugLog('Consent saved', record)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MEUI Cookies] Failed to save consent:', error)
  }
}

/**
 * Load consent from localStorage
 */
export function loadConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null

  try {
    const data = localStorage.getItem(STORAGE_KEYS.CONSENT)
    if (!data) return null

    const record: ConsentRecord = JSON.parse(data)
    debugLog('Consent loaded', record)
    return record
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MEUI Cookies] Failed to load consent:', error)
    return null
  }
}

/**
 * Check if consent exists
 */
export function hasConsent(): boolean {
  return loadConsent() !== null
}

/**
 * Clear consent from storage
 */
export function clearConsent(domain?: string): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(STORAGE_KEYS.CONSENT)
    localStorage.removeItem(STORAGE_KEYS.VERSION)
    deleteCookie(STORAGE_KEYS.CONSENT, domain)

    debugLog('Consent cleared')
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MEUI Cookies] Failed to clear consent:', error)
  }
}

/**
 * Check if consent is expired based on TTL
 */
export function isConsentExpired(record: ConsentRecord, ttl: number): boolean {
  const expiryTime = record.timestamp + ttl * 24 * 60 * 60 * 1000
  const isExpired = Date.now() > expiryTime

  if (isExpired) {
    debugLog('Consent expired', { record, ttl })
  }

  return isExpired
}

/**
 * Save remote config to cache
 */
export function saveConfigCache(config: RemoteConfig, ttl: number): void {
  if (typeof window === 'undefined') return

  try {
    const cache = {
      config,
      timestamp: Date.now(),
      ttl,
    }

    localStorage.setItem(STORAGE_KEYS.CONFIG_CACHE, JSON.stringify(cache))
    debugLog('Config cached', cache)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MEUI Cookies] Failed to save config cache:', error)
  }
}

/**
 * Load remote config from cache
 */
export function loadConfigCache(maxAge: number): RemoteConfig | null {
  if (typeof window === 'undefined') return null

  try {
    const data = localStorage.getItem(STORAGE_KEYS.CONFIG_CACHE)
    if (!data) return null

    const cache = JSON.parse(data)
    const age = Date.now() - cache.timestamp

    if (age > maxAge) {
      debugLog('Config cache expired', { age, maxAge })
      localStorage.removeItem(STORAGE_KEYS.CONFIG_CACHE)
      return null
    }

    debugLog('Config loaded from cache', cache)
    return cache.config
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MEUI Cookies] Failed to load config cache:', error)
    return null
  }
}

/**
 * Check if consent version changed (requires re-prompt)
 */
export function hasVersionChanged(currentVersion: string, storedVersion: string): boolean {
  const changed = currentVersion !== storedVersion

  if (changed) {
    debugLog('Version changed, re-prompt required', { currentVersion, storedVersion })
  }

  return changed
}

/**
 * Export user consent data (for GDPR data requests)
 */
export function exportConsentData(): ConsentRecord | null {
  return loadConsent()
}
